import { SYNCED_TABLES, syncedTable, type SyncedTableName } from '@/db/db';
import { getLocal, setLocal } from '@/db/repo';
import { SYNC_PUSH_MAX_BYTES, SYNC_PUSH_MAX_RECORDS } from '@/constants/app';
import type { SyncedRecord } from '@/domain/types';

/**
 * Per-record sync against the Netlify functions. Everything works without it:
 * with no token the app is simply local-only, and anything done offline is
 * picked up by the next successful sync.
 */

export type SyncState = 'offline' | 'signed-out' | 'syncing' | 'synced' | 'pending' | 'error';

export interface SyncOutcome {
  state: SyncState;
  pushed: number;
  pulled: number;
  rejected: number;
  message?: string;
}

interface PullResponse {
  changes: Partial<Record<SyncedTableName, SyncedRecord[]>>;
  cursor: Record<string, number>;
  count: number;
  now: number;
}

interface PushResponse {
  results: Partial<Record<SyncedTableName, Array<{ id: string; updatedAt: number; accepted: boolean }>>>;
  cursor: Record<string, number>;
  accepted: number;
  rejected: number;
  now: number;
}

export class SyncError extends Error {
  constructor(
    message: string,
    readonly state: SyncState,
  ) {
    super(message);
  }
}

async function endpoint(name: string): Promise<string> {
  const local = await getLocal();
  const base = local.serverUrl.replace(/\/+$/, '');
  return `${base}/.netlify/functions/${name}`;
}

export async function signIn(passcode: string): Promise<void> {
  const response = await fetch(await endpoint('auth'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ passcode }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new SyncError(describeAuthError(response.status, body), 'error');
  }

  const { token, expiresAt } = (await response.json()) as { token: string; expiresAt: number };
  await setLocal({ token, tokenExpiresAt: expiresAt });
}

function describeAuthError(status: number, body: { error?: string; message?: string }): string {
  if (status === 401) return 'That passcode was not right.';
  if (status === 429) return 'Too many attempts. Wait a minute and try again.';
  if (status === 503) {
    // The server's own message names the two environment variables, which is
    // the right thing for an API to say and the wrong thing to show someone
    // standing in front of the app. Tell him what to do instead.
    return 'Sync has not been set up yet. On the computer with the project, run "node scripts/setup-sync.mjs", then deploy once. Everything works offline on this device until then.';
  }
  return body.message ?? `Sign-in failed (${status}).`;
}

export async function signOut(): Promise<void> {
  await setLocal({ token: null, tokenExpiresAt: null });
}

export async function isSignedIn(): Promise<boolean> {
  const local = await getLocal();
  return Boolean(local.token) && (local.tokenExpiresAt ?? 0) > Date.now();
}

/** Records this device has changed since its last successful push. */
export async function pendingCount(): Promise<number> {
  const local = await getLocal();
  let total = 0;
  for (const table of SYNCED_TABLES) {
    total += await syncedTable(table).where('updatedAt').above(local.lastPushedAt).count();
  }
  return total;
}

let running: Promise<SyncOutcome> | null = null;

/** Only one sync at a time; a second caller joins the one in flight. */
export function runSync(): Promise<SyncOutcome> {
  if (running) return running;
  running = doSync().finally(() => {
    running = null;
  });
  return running;
}

async function doSync(): Promise<SyncOutcome> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { state: 'offline', pushed: 0, pulled: 0, rejected: 0 };
  }
  if (!(await isSignedIn())) {
    return { state: 'signed-out', pushed: 0, pulled: 0, rejected: 0 };
  }

  try {
    const pushed = await pushChanges();
    const pulled = await pullChanges();
    await setLocal({ lastSyncAt: Date.now() });
    const outstanding = await pendingCount();
    return {
      state: outstanding > 0 ? 'pending' : 'synced',
      pushed: pushed.pushed,
      pulled,
      rejected: pushed.rejected,
    };
  } catch (err) {
    if (err instanceof SyncError) {
      return { state: err.state, pushed: 0, pulled: 0, rejected: 0, message: err.message };
    }
    return {
      state: 'error',
      pushed: 0,
      pulled: 0,
      rejected: 0,
      message: (err as Error).message,
    };
  }
}

async function authHeaders(): Promise<HeadersInit> {
  const local = await getLocal();
  return { 'content-type': 'application/json', authorization: `Bearer ${local.token}` };
}

async function handleAuthFailure(response: Response): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  if (body.error === 'passcode_changed' || body.error === 'expired' || body.error === 'bad_signature') {
    await signOut();
    throw new SyncError('Signed out — sign in again with your passcode.', 'signed-out');
  }
  throw new SyncError(`Sync rejected (${response.status}).`, 'error');
}

// ---------------------------------------------------------------------- push

async function collectChanges(since: number): Promise<Array<Partial<Record<SyncedTableName, SyncedRecord[]>>>> {
  const batches: Array<Partial<Record<SyncedTableName, SyncedRecord[]>>> = [];
  let current: Partial<Record<SyncedTableName, SyncedRecord[]>> = {};
  let count = 0;
  let bytes = 0;

  const flush = () => {
    if (count > 0) batches.push(current);
    current = {};
    count = 0;
    bytes = 0;
  };

  for (const table of SYNCED_TABLES) {
    const rows = (await syncedTable(table)
      .where('updatedAt')
      .above(since)
      .toArray()) as unknown as SyncedRecord[];

    for (const row of rows) {
      const size = JSON.stringify(row).length;
      // Stay well inside Netlify's synchronous function payload limit.
      if (count >= SYNC_PUSH_MAX_RECORDS || bytes + size > SYNC_PUSH_MAX_BYTES) flush();
      const list = current[table] ?? [];
      list.push(row);
      current[table] = list;
      count += 1;
      bytes += size;
    }
  }
  flush();
  return batches;
}

async function pushChanges(): Promise<{ pushed: number; rejected: number }> {
  const local = await getLocal();
  const batches = await collectChanges(local.lastPushedAt);
  if (batches.length === 0) return { pushed: 0, rejected: 0 };

  let pushed = 0;
  let rejected = 0;
  let highest = local.lastPushedAt;

  for (const batch of batches) {
    const response = await fetch(await endpoint('sync-push'), {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ changes: batch }),
    });
    if (response.status === 401) await handleAuthFailure(response);
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      throw new SyncError(body.message ?? `Push failed (${response.status}).`, 'error');
    }

    const result = (await response.json()) as PushResponse;
    pushed += result.accepted;
    rejected += result.rejected;

    for (const [table, rows] of Object.entries(batch) as Array<[SyncedTableName, SyncedRecord[]]>) {
      const outcomes = new Map((result.results[table] ?? []).map((o) => [o.id, o]));
      for (const row of rows) {
        highest = Math.max(highest, row.updatedAt);
        const outcome = outcomes.get(row.id);
        if (!outcome) continue;
        highest = Math.max(highest, outcome.updatedAt);
        // The server clamps a timestamp from a clock running fast; take its
        // word for it so the two sides agree on what "newest" means.
        if (outcome.accepted && outcome.updatedAt !== row.updatedAt) {
          await syncedTable(table).put({ ...row, updatedAt: outcome.updatedAt } as never);
        }
      }
    }
  }

  await setLocal({ lastPushedAt: highest });
  return { pushed, rejected };
}

// ---------------------------------------------------------------------- pull

async function pullChanges(): Promise<number> {
  let pulled = 0;

  // Keep pulling while the server is handing back full pages.
  for (let page = 0; page < 50; page += 1) {
    const local = await getLocal();
    const url = new URL(await endpoint('sync-pull'), window.location.origin);
    if (local.syncCursor) url.searchParams.set('cursor', local.syncCursor);

    const response = await fetch(url.toString(), { headers: await authHeaders() });
    if (response.status === 401) await handleAuthFailure(response);
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      throw new SyncError(body.message ?? `Pull failed (${response.status}).`, 'error');
    }

    const result = (await response.json()) as PullResponse;
    pulled += await applyRemoteChanges(result.changes);
    await setLocal({ syncCursor: JSON.stringify(result.cursor) });

    if (result.count === 0) break;
  }

  return pulled;
}

/** Last write wins, compared record by record against what is stored here. */
async function applyRemoteChanges(
  changes: Partial<Record<SyncedTableName, SyncedRecord[]>>,
): Promise<number> {
  let applied = 0;

  for (const [name, rows] of Object.entries(changes) as Array<[SyncedTableName, SyncedRecord[]]>) {
    if (!SYNCED_TABLES.includes(name)) continue;
    if (!rows?.length) continue;

    const table = syncedTable(name);
    const existing = await table.bulkGet(rows.map((r) => r.id));
    const toWrite: SyncedRecord[] = [];

    rows.forEach((row, i) => {
      const current = existing[i];
      if (!current || row.updatedAt > current.updatedAt) toWrite.push(row);
    });

    if (toWrite.length) {
      await table.bulkPut(toWrite as never[]);
      applied += toWrite.length;
    }
  }

  return applied;
}

/** Wipes local sync bookkeeping so the next sync re-reads everything. */
export async function resetSyncCursor(): Promise<void> {
  await setLocal({ syncCursor: '', lastPushedAt: 0 });
}
